import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { searchProducts, getBrandsWithImages } from "../services/productService";
import { getCategories } from "../services/categoryService";
import { getPageBySlug } from "../services/pageService";
import PageRenderer from "../components/PageRenderer";

import "../styles/productCard.css";
import { resolveAssetUrl } from "../utils/resolveUrl";
import ProductCard from "../components/ProductCard";

export default function Home() {

  /* ---------------- Dynamic page ---------------- */
  const [dynamicPage, setDynamicPage] = useState(null);
  const [loadingPage, setLoadingPage] = useState(true);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    getPageBySlug("home")
      .then((p) => {
        if (p) {
          setDynamicPage(p);
        } else {
          // Only fetch fallback data if no dynamic page is found
          searchProducts({ limit: 12 }).then(data => {
            if (Array.isArray(data)) setProducts(data);
            else console.error("searchProducts returned non-array:", data);
          }).catch(err => console.error(err));

          getBrandsWithImages().then(res => setBrands(res || [])).catch(console.error);

          getCategories().then((c) => setCategories(Array.isArray(c) ? c : []));
        }
      })
      .finally(() => setLoadingPage(false));
  }, []);

  /* ---------------- Sliders ---------------- */
  const popularViewportRef = useRef(null);
  const popularTrackRef = useRef(null);
  const [popularIdx, setPopularIdx] = useState(0);
  const [isTransitioningPopular, setIsTransitioningPopular] = useState(true);
  // Responsive visible items state moved to top level
  const [visibleItems, setVisibleItems] = useState(6);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 480) setVisibleItems(1);
      else if (window.innerWidth < 768) setVisibleItems(2);
      else if (window.innerWidth < 1100) setVisibleItems(3);
      else if (window.innerWidth < 1440) setVisibleItems(4);
      else setVisibleItems(5);
    };
    handleResize(); // initial
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const rawList = products.slice(0, 12);
  const totalRaw = rawList.length;
  const repeatCountPopular = Math.max(5, Math.ceil((visibleItems * 4) / Math.max(1, totalRaw)));
  const displayPopular = totalRaw > visibleItems ? Array(repeatCountPopular).fill(rawList).flat() : rawList;
  const totalRenderedPopular = displayPopular.length;

  useEffect(() => {
    if (totalRaw > visibleItems) {
      setPopularIdx(Math.floor(repeatCountPopular / 2) * totalRaw);
    } else {
      setPopularIdx(0);
    }
  }, [totalRaw, visibleItems, repeatCountPopular]);

  const handlePopularTransitionEnd = () => {
    if (totalRaw <= visibleItems) return;
    const middle = Math.floor(repeatCountPopular / 2) * totalRaw;
    if (popularIdx >= (repeatCountPopular - 2) * totalRaw) {
      setIsTransitioningPopular(false);
      setPopularIdx(middle + (popularIdx % totalRaw));
    } else if (popularIdx <= totalRaw) {
      setIsTransitioningPopular(false);
      setPopularIdx(middle + (popularIdx % totalRaw));
    }
  };

  const handlePopularPrev = () => {
    if (totalRaw <= visibleItems) return;
    const middle = Math.floor(repeatCountPopular / 2) * totalRaw;
    if (popularIdx <= totalRaw) {
      setIsTransitioningPopular(false);
      const resetIndex = middle + (popularIdx % totalRaw);
      setPopularIdx(resetIndex);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioningPopular(true);
          setPopularIdx(resetIndex - 1);
        });
      });
    } else {
      setIsTransitioningPopular(true);
      setPopularIdx((prev) => prev - 1);
    }
  };

  const handlePopularNext = () => {
    if (totalRaw <= visibleItems) return;
    const middle = Math.floor(repeatCountPopular / 2) * totalRaw;
    const maxSafe = (repeatCountPopular - 2) * totalRaw;
    if (popularIdx >= maxSafe) {
      setIsTransitioningPopular(false);
      const resetIndex = middle + (popularIdx % totalRaw);
      setPopularIdx(resetIndex);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioningPopular(true);
          setPopularIdx(resetIndex + 1);
        });
      });
    } else {
      setIsTransitioningPopular(true);
      setPopularIdx((prev) => prev + 1);
    }
  };

  const dragStartXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const hasMovedRef = useRef(false);

  const onPopularPointerDown = (e) => {
    if (totalRaw <= visibleItems) return;
    const clientX = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    dragStartXRef.current = clientX;
    isDraggingRef.current = true;
    hasMovedRef.current = false;
  };

  const onPopularPointerMove = (e) => {
    if (!isDraggingRef.current) return;
    const clientX = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    if (Math.abs(clientX - dragStartXRef.current) > 8) {
      hasMovedRef.current = true;
    }
  };

  const onPopularPointerUp = (e) => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    const endX = e.clientX ?? (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : dragStartXRef.current);
    const diffX = endX - dragStartXRef.current;
    if (Math.abs(diffX) >= 40) {
      if (diffX < 0) {
        handlePopularNext();
      } else {
        handlePopularPrev();
      }
    }
  };

  /* ---------------- Loading ---------------- */
  if (loadingPage) return null;

  /* ---------------- CMS page ---------------- */
  if (dynamicPage?.content) {
    return (
      <div className="page-container">
        <PageRenderer content={Array.isArray(dynamicPage.content) ? dynamicPage.content.filter(m => !(m.type === 'announcement' && m.config && m.config.text && m.config.text.includes('LOREM'))) : dynamicPage.content} />
      </div>
    );
  }

  /* ========================================================= */
  /* ======================= FALLBACK ======================== */
  /* ========================================================= */

  return (
    <div className="page-container">
      {/* ================= POPULAR PRODUCTS ================= */}
      <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-center my-6 sm:my-10">
        Produkte të rekomanduara
      </h2>

      <div style={{ width: "100%" }}>
        {(() => {
          if (totalRaw === 0) return null;

          return (
            <div style={{ position: "relative" }}>
              <div
                ref={popularViewportRef}
                onMouseDown={onPopularPointerDown}
                onMouseMove={onPopularPointerMove}
                onMouseUp={onPopularPointerUp}
                onMouseLeave={onPopularPointerUp}
                onTouchStart={onPopularPointerDown}
                onTouchMove={onPopularPointerMove}
                onTouchEnd={onPopularPointerUp}
                onTouchCancel={onPopularPointerUp}
                style={{ overflow: "hidden", borderRadius: 10, cursor: totalRaw > visibleItems ? 'grab' : 'default', touchAction: 'pan-y', userSelect: 'none' }}
              >
                <div
                  ref={popularTrackRef}
                  onTransitionEnd={handlePopularTransitionEnd}
                  onClickCapture={(e) => {
                    if (hasMovedRef.current) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  style={{
                    display: "flex",
                    width: totalRenderedPopular > 0 ? `${(100 * totalRenderedPopular) / visibleItems}%` : "100%",
                    transform: `translateX(${totalRenderedPopular > 0 ? -((popularIdx * 100) / totalRenderedPopular) : 0}%)`,
                    transition: isTransitioningPopular ? "transform 400ms ease" : "none",
                  }}
                >
                  {displayPopular.map((p, idx) => {
                    return (
                      <div
                        key={`${p.id || 'prod'}-${idx}`}
                        style={{
                          width: totalRenderedPopular > 0 ? `${100 / totalRenderedPopular}%` : "100%",
                          padding: 8,
                        }}
                      >
                        <ProductCard product={p} />
                      </div>
                    );
                  })}
                </div>
              </div>

              {totalRaw > visibleItems && (
                <>
                  <button
                    type="button"
                    className="glb-slider-nav-btn"
                    onClick={handlePopularPrev}
                    style={{
                      position: "absolute",
                      left: 10,
                      top: "45%",
                      zIndex: 10
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                  </button>
                  <button
                    type="button"
                    className="glb-slider-nav-btn"
                    onClick={handlePopularNext}
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "45%",
                      zIndex: 10
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                  </button>
                </>
              )}
            </div>
          );
        })()}
      </div>

      {/* ================= CATEGORIES ================= */}
      <section className="mt-8 sm:mt-12">
        <h2 className="text-2xl sm:text-2xl md:text-3xl font-semibold text-center mb-4" style={{ color: '#094859' }}>Produkte sipas kategorive</h2>

        <div
          style={{
            maxWidth: 1400,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))",
            gap: 20,
          }}
        >
          {categories.slice(0, 6).map((cat) => (
            <div
              key={cat.id}
              style={{
                border: "1px solid #eee",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <img
                src={resolveAssetUrl(cat.image)}
                alt={cat.name}
                style={{ width: "100%", height: 220, objectFit: "cover" }}
              />
              <div style={{ padding: 14 }}>
                <strong>{cat.name}</strong>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= BRANDS ================= */}
      <section className="mt-8 sm:mt-12">
        <h2 className="text-2xl sm:text-2xl md:text-3xl font-semibold text-center mb-4" style={{ color: '#094859' }}>Markat</h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))",
            gap: 20,
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          {brands.map((b) => (
            <Link
              key={b.name}
              to={`/products?brand=${encodeURIComponent(b.name)}`}
            >
              <div style={{ textAlign: "center" }}>
                <img
                  src={resolveAssetUrl(b.image)}
                  alt={b.name}
                  style={{
                    width: "100%",
                    height: 120,
                    objectFit: "contain",
                  }}
                />
                <div>{b.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
